import { InfiniteData } from '@tanstack/react-query';
import {
  Cell,
  Column,
  ColumnDef,
  Header,
  Row,
  Table,
} from '@tanstack/react-table';
import { getStartOfRangeHorizon, RangeHorizon } from '@toggle/chart';
import { deepEqual } from '@toggle/helpers';
import {
  ColumnMeta,
  FilterResultMethod,
  FilterResults,
  FilterResultsKeyData,
  MappedEntity,
  MetaValueType,
  TSDatum,
} from '@toggle/toggle';
import { Property } from 'csstype';
import { differenceInBusinessDays } from 'date-fns';
import { t } from 'i18next';
import React from 'react';

import { EntityCell } from '~/components/entity-cell/EntityCell';
import { ActiveFilterOptions } from '~/components/filters/Filters';
import { OneDayReturnCell } from '~/components/one-day-return-cell/OneDayReturnCell';
import { PriceCell } from '~/components/price-cell/PriceCell';
import { FilterGroups } from '~/hooks/use-filter-actions/useFilterActions';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import { InsightsKGCell } from '~/views/dashboard/my-assets-widget/assets-table/insights-cell/insights-kg-cell/InsightsKGCell';
import { InsightsScenarioCell } from '~/views/dashboard/my-assets-widget/assets-table/insights-cell/insights-scenario-cell/InsightsScenarioCell';

import { ScreenerCell } from '../screener-cell/ScreenerCell';
import { checkIfHorizonsMatch } from '../screener-table/screener-table-header-cell/utils';
import { ScreenerTableChart } from '../screener-table-chart/ScreenerTableChart';
import { LIVE_ONE_DAY_RETURN_COLUMN_META_VALUE_FIELD } from './constants';
import styles from './Results.module.scss';
import { SCREENER_ASSET_COLUMN_ID } from './screener-events-table/utils/column-utils';
import { ScreenerEvents } from './screener-events-table/utils/types';
import { KeyDevelopments } from './screener-key-developments-table/utils/types';
import { getColumnFilterKeyDetails } from './use-table-actions/utils';
import { ScreenerTableData } from './useScreenerTable';

export interface SavedCategorySetting extends Record<string, string[]> {}

export interface PinnedData {
  categoryData: FilterResultsKeyData;
  fields: string[];
}

export interface ColumnOrder {
  key: string;
  fields: string[];
}

export interface ColumnMetaWithKey extends ColumnMeta {
  key: FilterResultsKeyData['category'];
}

export const DEFAULT_TAB = 'overview';
export const KNOWLEDGE_GRAPH_TAB = 'knowledge_graph';
export const KEY_DEV_TAB = 'key_developments';

export const PRICE_COLUMN_META_VALUE_FIELD = 'price.value';
export const LIVE_PRICE_COLUMN_META_VALUE_FIELD = 'latest_price.value';
export const PREV_DAY_RETURN_COLUMN_META_VALUE_FIELD = 'mom(horiz=1d).value';

export const SCREENER_INSIGHTS_CATALYSTS_COLUMN_ID = 'catalysts1W';
export const SCREENER_INSIGHTS_SCENARIO_COLUMN_ID = 'insightsScenario1W';

const numFormatter = new Intl.NumberFormat(navigator.language, {
  notation: 'compact',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const priceFormatter = new Intl.NumberFormat(navigator.language, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const mapValueTypeToFormatter: Record<
  MetaValueType,
  (value: number) => string
> = {
  float: priceFormatter.format,
  scaled_number: numFormatter.format,
  integer: v => v.toString(),
};

export const isColumnMetaWithKey = (
  meta: unknown
): meta is ColumnMetaWithKey => {
  if (typeof meta !== 'object' || meta === null) {
    return false;
  }

  return 'field' in meta && typeof meta.field === 'string';
};

const hasEntity = <T,>(
  data: T
): data is T & { entity: ScreenerTableData['entity'] } =>
  typeof data === 'object' && data !== null && 'entity' in data;

const isColumnMeta = (meta: unknown): meta is ColumnMeta => {
  if (typeof meta !== 'object' || meta === null) {
    return false;
  }

  return 'field' in meta && 'filter' in meta && 'name' in meta;
};

export const sortingFn = <T,>(rowA: Row<T>, rowB: Row<T>, columnId: string) => {
  const a = rowA.getValue<FilterResultMethod>(columnId).value;
  const b = rowB.getValue<FilterResultMethod>(columnId).value;

  // similar to SortingColumnDef.sortingUndefined:"last"
  if (a === undefined) {
    return 1;
  }

  if (b === undefined) {
    return -1;
  }

  return a > b ? 1 : a < b ? -1 : 0;
};

export const mapColumnMeta = <T extends Record<string, FilterResultMethod>>(
  c: ColumnMetaWithKey | ColumnMeta,
  isDisabled?: boolean
): ColumnDef<T> => ({
  id: c.field,
  accessorFn: row => row[c.field],
  header: c.name,
  cell: ({ getValue, row }) => {
    const isPriceCell = c.field === LIVE_PRICE_COLUMN_META_VALUE_FIELD;
    const isOneDayReturnCell =
      c.field === LIVE_ONE_DAY_RETURN_COLUMN_META_VALUE_FIELD;
    const value = getValue<FilterResultMethod>();
    const rowData = row.original;

    if (isPriceCell && hasEntity(rowData)) {
      return <PriceCell asset={rowData.entity} />;
    }

    if (isOneDayReturnCell && hasEntity(rowData)) {
      return <OneDayReturnCell asset={rowData.entity} />;
    }

    return (
      <ScreenerCell value={value} columnMeta={c} isDisabled={isDisabled} />
    );
  },
  meta: c,
  sortingFn,
});

export const getColumns = ({
  columnsMeta,
  horizon,
  source,
  isCatalystInsightsEnabled = false,
  isEarningsInsightsEnabled = false,
}: {
  columnsMeta: ColumnMetaWithKey[];
  horizon: RangeHorizon;
  source: PosthogTrackingSource;
  isCatalystInsightsEnabled?: boolean;
  isEarningsInsightsEnabled?: boolean;
}) => {
  const entityColumnDef: ColumnDef<ScreenerTableData> = {
    id: SCREENER_ASSET_COLUMN_ID,
    header: t('widget:watchlist.asset'),
    meta: {
      alignment: 'start',
    },
    cell: props => {
      const item = props.row.original;
      return (
        item.entity && (
          <div className={styles.cellWithChartContainer}>
            <div className={styles.assetCell}>
              <EntityCell entityTag={item.entity.tag} />
            </div>
            {!!item.ts?.length && (
              <ScreenerTableChart
                width={40}
                height={24}
                data={item.ts}
                entity={item.entity}
                horizon={horizon}
              />
            )}
          </div>
        )
      );
    },
  };

  const catalystsColumnDef: ColumnDef<ScreenerTableData> = {
    id: SCREENER_INSIGHTS_CATALYSTS_COLUMN_ID,
    header: t('widget:watchlist.catalysts1W'),
    meta: {
      alignment: 'start',
    },
    cell: props => (
      <InsightsKGCell
        entityTag={props.row.original.entity.tag}
        source={source}
        isDisabled={false}
      />
    ),
    enableSorting: false,
  };

  const scenariosColumnDef: ColumnDef<ScreenerTableData> = {
    id: SCREENER_INSIGHTS_SCENARIO_COLUMN_ID,
    header: t('widget:watchlist.scenario1W'),
    meta: {
      alignment: 'start',
    },
    cell: props => {
      const item = props.row.original;
      return (
        <InsightsScenarioCell
          entityTag={item.entity.tag}
          isDisabled={false}
          source="screener"
        />
      );
    },
    enableSorting: false,
  };

  const hasParent = columnsMeta.some(c => c.parent_id);

  const shouldShowCatalysts =
    isCatalystInsightsEnabled || isEarningsInsightsEnabled;

  const pinnedColumns = [
    entityColumnDef,
    ...(shouldShowCatalysts ? [catalystsColumnDef] : []),
    scenariosColumnDef,
  ];

  if (hasParent) {
    const mapIdToColumnsMeta = columnsMeta.reduce<
      Record<
        string,
        { id: string; name: string; columnsMeta: ColumnMetaWithKey[] }
      >
    >((res, next) => {
      const parentId = next.parent_id ?? next.field;
      const name = next.parent_name ?? next.name;

      if (res[parentId]) {
        res[parentId].columnsMeta.push(next);
      } else {
        res[parentId] = {
          id: parentId,
          name,
          columnsMeta: [next],
        };
      }

      return res;
    }, {});

    const parentColumnDefs = Object.values(mapIdToColumnsMeta).map(item => ({
      id: item.id,
      header: item.name,
      columns: item.columnsMeta.map<ColumnDef<ScreenerTableData>>(cm =>
        mapColumnMeta(cm)
      ),
    }));
    return [
      ...pinnedColumns.map(c => ({
        id: c.id ?? '',
        header: c.header,
        columns: [c],
      })),
      ...parentColumnDefs,
    ];
  }

  const columns: ColumnDef<ScreenerTableData>[] = [
    ...pinnedColumns,
    ...columnsMeta.map<ColumnDef<ScreenerTableData>>(cm => mapColumnMeta(cm)),
  ];

  return columns;
};

export interface FilterQueryResultData {
  filterResults: FilterResults;
  tickerColumnData: {
    mappedEntities: MappedEntity[];
    ts: Array<TSDatum[] | undefined>;
  };
}

export interface TableData {
  [category: string]: {
    lastPage: number;
    data: ScreenerTableData[];
  };
}

const mapFilterQueryResult = ({
  pageData,
  categoryData,
  pinnedColumnsMeta,
}: {
  pageData: FilterQueryResultData;
  categoryData: FilterResultsKeyData;
  pinnedColumnsMeta: ColumnMetaWithKey[];
}) => {
  const { tickerColumnData, filterResults } = pageData;

  const pinnedCategoriesData = pinnedColumnsMeta
    .map(p => {
      const foundCategoryData = pageData.filterResults.data.find(
        item => item.key === p.key
      );

      return foundCategoryData
        ? { categoryData: foundCategoryData, field: p.field }
        : null;
    })
    .filter(
      (item): item is { categoryData: FilterResultsKeyData; field: string } =>
        item !== null
    );

  return (
    categoryData.data?.reduce<ScreenerTableData[]>((res, item, idx) => {
      const entityTag = filterResults.entities[idx];
      const orderIndex = tickerColumnData.mappedEntities.findIndex(
        ({ tag }) => tag === entityTag
      );
      const entity = tickerColumnData.mappedEntities[orderIndex];

      if (entity) {
        const ts = tickerColumnData.ts[orderIndex];
        const tableRow: ScreenerTableData = Object.assign({ entity, ts }, item);

        pinnedCategoriesData.forEach(pinnedItem => {
          tableRow[pinnedItem.field] =
            pinnedItem.categoryData.data[idx][pinnedItem.field];
        });

        res.push(tableRow);
      }

      return res;
    }, []) || []
  );
};

export const tableDataQueryUpdater =
  ({
    data,
    tab,
    pinnedColumnsMeta,
  }: {
    data: InfiniteData<FilterQueryResultData>;
    tab: string;
    pinnedColumnsMeta: ColumnMetaWithKey[];
  }) =>
  (prevData: TableData = {}) => {
    //TO-DO figure out a way not to map every row on each tab switch
    // prev logic doesn't work when pinning columns
    //consider using virtualized list
    // const prevItems = prevData[tab];
    // const hasAllData = !!prevItems && prevItems.lastPage === data.pages.length;
    // if (hasAllData) {
    //   return prevData;
    // }

    const newData = data.pages.flatMap(pageData => {
      const categoryData = pageData.filterResults.data.find(d => d.key === tab);

      if (!categoryData) {
        return [];
      }

      return mapFilterQueryResult({
        pageData,
        categoryData,
        pinnedColumnsMeta,
      });
    });

    return {
      ...prevData,
      [tab]: {
        data: newData,
        lastPage: data.pages.length,
      },
    };
  };

export const getNumDaysForHorizon = (
  horizon: RangeHorizon,
  start = new Date()
) => {
  const horizonStartDate = getStartOfRangeHorizon(horizon, start);
  return horizonStartDate
    ? differenceInBusinessDays(start, horizonStartDate)
    : 21;
};

export const getPinnedColumnsMeta = ({
  filterResults,
  pinnedColumns,
  categoryData,
}: {
  filterResults: FilterResults;
  pinnedColumns: SavedCategorySetting;
  categoryData?: FilterResultsKeyData;
  resetToDefault?: boolean;
}) => {
  // we only add pinned columns to the overview tab
  // if resetting we want to remove all pinned columns from overview
  if (categoryData?.key !== DEFAULT_TAB) {
    return [];
  }

  const pinnedColumnsMeta = Object.keys(pinnedColumns).reduce<
    ColumnMetaWithKey[]
  >((res, key) => {
    const fields = pinnedColumns[key];
    const categoryData = filterResults.data.find(item => item.key === key);

    if (categoryData) {
      fields.forEach(field => {
        const meta = categoryData.meta.find(m => m.field === field);

        if (meta) {
          res.push({ ...meta, key });
        }
      });
    }

    return res;
  }, []);

  return pinnedColumnsMeta;
};

const isParentHighlighted = (
  table: Table<ScreenerTableData>,
  parent: Column<ScreenerTableData, unknown>
) => {
  const headerGroup = table.getHeaderGroups()[0];
  return (
    headerGroup.headers.findIndex(c => c.column.id === parent.id) % 2 !== 0
  );
};

export const getIsCellHighlighted = (
  cell: Cell<ScreenerTableData, unknown>
) => {
  const parent = cell.column.parent;

  if (parent) {
    return isParentHighlighted(cell.getContext().table, parent);
  }

  return cell.column.getIndex() % 2 !== 0;
};

export const getIsHeaderHighlighted = (
  header: Header<ScreenerTableData, unknown>
) => {
  const parent = header.column.parent;

  if (parent) {
    return isParentHighlighted(header.getContext().table, parent);
  }

  return header.index % 2 !== 0;
};

export const menuItems = [
  {
    key: 'themeOverview',
    iconName: 'ChevronLightRight',
    group: 'themeOverview',
  } as const,
  {
    key: 'ascending',
    iconName: 'SortUp',
    group: 'sort',
  } as const,
  {
    key: 'descending',
    iconName: 'SortDown',
    group: 'sort',
  } as const,
  {
    key: 'clearSort',
    iconName: 'ClearSort',
    group: 'sort',
  } as const,
  { key: 'ascendingDocumentType', iconName: 'SortUp', group: 'sort' } as const,
  {
    key: 'descendingDocumentType',
    iconName: 'SortDown',
    group: 'sort',
  } as const,
  {
    key: 'filter',
    iconName: 'FilterInline',
    group: 'filter',
  } as const,
  {
    key: 'editFilter',
    iconName: 'FilterInline',
    group: 'filter',
  } as const,
  {
    key: 'clearFilter',
    iconName: 'FilterClear',
    group: 'filter',
  } as const,
  {
    key: 'pin',
    iconName: 'Pin',
    group: 'pin',
  } as const,
  {
    key: 'unpin',
    iconName: 'Unpin',
    group: 'pin',
  } as const,
  {
    key: 'left',
    iconName: 'ArrowLeftLong',
    group: 'move',
  } as const,
  {
    key: 'right',
    iconName: 'ArrowRightLong',
    group: 'move',
  } as const,
  {
    key: 'start',
    iconName: 'ArrowLeftLongLine',
    group: 'move',
  } as const,
  {
    key: 'end',
    iconName: 'ArrowRightLongLine',
    group: 'move',
  } as const,
  {
    key: 'reset',
    iconName: 'ArrowRotateRight',
    group: 'reset',
  } as const,
];

export const defaultDropdownKeys = menuItems.map(item => item.key);
export const navigableDropdownOptions = ['themeOverview'];

export type TableAction = (typeof menuItems)[number]['key'];

// eslint-disable-next-line complexity
export const getDisabledKeys = ({
  pinnedFields,
  header,
  lastColumnIndex,
  overviewColumns,
  activeFilterOptions,
}: {
  pinnedFields: string[];
  lastColumnIndex: number;
  header: Header<ScreenerTableData, unknown>;
  overviewColumns: string[];
  activeFilterOptions: ActiveFilterOptions[];
}): TableAction[] => {
  const column = header.column;

  if (column.parent && column.parent.id !== column.id) {
    return defaultDropdownKeys.filter(key =>
      key === 'ascending' || key === 'descending' ? false : true
    );
  }

  let defaultKeys: TableAction[] = [
    'clearSort',
    'ascendingDocumentType',
    'descendingDocumentType',
  ];

  if (
    column.id === SCREENER_ASSET_COLUMN_ID ||
    column.id === SCREENER_INSIGHTS_CATALYSTS_COLUMN_ID ||
    column.id === SCREENER_INSIGHTS_SCENARIO_COLUMN_ID
  ) {
    const enableKeys = ['ascending', 'descending'];

    defaultKeys = defaultDropdownKeys.filter(key => !enableKeys.includes(key));
  }

  const groupColumns = column.columns.length ? column.columns : [column];
  const childMeta = column.columns?.[0]?.columnDef?.meta;
  const childColumnDefMeta = isColumnMetaWithKey(childMeta)
    ? childMeta
    : undefined;
  const meta = column.columnDef.meta;
  const columnDefMeta = isColumnMetaWithKey(meta) ? meta : undefined;
  const { headerFilter, isMultiColumnFilter } = getHeaderFilter(
    header,
    activeFilterOptions
  );
  const horizonsMatch = checkIfHorizonsMatch(columnDefMeta, headerFilter);
  const disableApplyFilterKey = !isMultiColumnFilter
    ? !headerFilter
    : horizonsMatch;

  //handles same columns as on the overview tab
  if (
    columnDefMeta?.key === DEFAULT_TAB ||
    overviewColumns.includes(column.id)
  ) {
    defaultKeys.push('pin', 'unpin');
  } else {
    defaultKeys.push(
      pinnedFields.some(id => groupColumns.some(c => c.id === id))
        ? 'pin'
        : 'unpin'
    );
  }

  if (childColumnDefMeta?.filter?.split('.')?.[0] !== 'theme_exposure') {
    defaultKeys.push('themeOverview');
  }

  if (disableApplyFilterKey) {
    defaultKeys.push('filter');
  } else {
    defaultKeys.push('editFilter');
    defaultKeys.push('clearFilter');
  }

  //parent column with a different column than parent.column
  if (
    header.subHeaders.length &&
    header.subHeaders[0].column.id !== column.id
  ) {
    defaultKeys.push(
      ...defaultDropdownKeys.filter(key =>
        ['ascending', 'descending', 'filter'].includes(key)
      )
    );
  }

  const idx = header.subHeaders.length ? header.index : column.getIndex();

  if (!!column.getIsSorted()) {
    defaultKeys = defaultKeys.filter(key => key !== 'clearSort');
  }

  //single parent column displayed
  if (idx === 1 && idx === lastColumnIndex) {
    return ['left', 'start', 'right', 'end', ...defaultKeys];
  }

  if (idx === 1) {
    return ['left', 'start', ...defaultKeys];
  }

  if (idx === lastColumnIndex) {
    return ['right', 'end', ...defaultKeys];
  }

  return defaultKeys;
};

export const isHeaderPinned = (
  header: Header<ScreenerTableData, unknown>,
  pinnedFields: string[]
) => {
  if (header.column.parent) {
    return false;
  }

  return header.column.columns.length
    ? pinnedFields.some(id => header.column.columns.some(c => c.id === id))
    : pinnedFields.includes(header.column.id);
};

export const getHeaderFilter = (
  header: Header<ScreenerTableData, unknown>,
  activeFilterOptions: ActiveFilterOptions[]
) => {
  const rawMeta = header.column.columnDef.meta;
  const meta = isColumnMeta(rawMeta) ? rawMeta : undefined;
  const { filterKey, isMultiColumnFilter } = getColumnFilterKeyDetails(meta);
  const filterOptions = activeFilterOptions.find(f => f.filter === filterKey);

  if (!filterOptions) {
    return {
      isMultiColumnFilter,
    };
  }

  const hasValidChildFilters = filterOptions.options.every(
    opt => !opt.childName || meta?.filter_children?.includes(opt.key)
  );
  const shouldReturnFilterOptions = hasValidChildFilters || isMultiColumnFilter;
  const headerFilter = shouldReturnFilterOptions ? filterOptions : undefined;

  return {
    headerFilter,
    isMultiColumnFilter,
  };
};

export const getCategoryData = (
  data: InfiniteData<FilterQueryResultData> | undefined,
  tab: string
) => {
  const latestData = data?.pages.at(-1);
  return latestData?.filterResults.data.find(d => d.key === tab);
};

type TabRedirectConfig = {
  group: 'Knowledge Graph Filters' | 'Market Filters';
  ids?: string[];
  tabFn: (id: string, categoryData?: FilterResultsKeyData) => string;
};

export const getEventsOrKeyDevsTab = ({
  filterGroups,
}: {
  filterGroups: FilterGroups<'Knowledge Graph Filters' | 'Market Filters'>;
}) => {
  const marketFilters = filterGroups['Market Filters'];

  const keyDevelopmentTypes: string[] = Object.values(KeyDevelopments);
  const keyDevsFilter = marketFilters.activeFilterOptions.find(filter =>
    keyDevelopmentTypes.includes(filter.id)
  );

  if (keyDevsFilter) {
    return KEY_DEV_TAB;
  }

  const eventsFilterTypes: string[] = Object.values(ScreenerEvents);
  const eventsFilter = marketFilters.activeFilterOptions.find(filter =>
    eventsFilterTypes.includes(filter.id)
  );

  if (eventsFilter) {
    return (
      marketFilters.allFilters.find(f => f.key === eventsFilter.id)?.key ||
      DEFAULT_TAB
    );
  }

  return DEFAULT_TAB;
};

export const getNewTableTab = ({
  prevFilterGroups,
  filterGroups,
  categoryData,
}: {
  categoryData?: FilterResultsKeyData;
  prevFilterGroups?: FilterGroups<'Knowledge Graph Filters' | 'Market Filters'>;
  filterGroups: FilterGroups<'Knowledge Graph Filters' | 'Market Filters'>;
}) => {
  const tabRedirectMapping: Record<string, TabRedirectConfig> = {
    knowledgeGraph: {
      group: 'Knowledge Graph Filters',
      tabFn: (_: string, categoryData?: FilterResultsKeyData) => {
        return categoryData ? KNOWLEDGE_GRAPH_TAB : DEFAULT_TAB;
      },
    },
    events: {
      group: 'Market Filters',
      ids: Object.values(ScreenerEvents),
      tabFn: (id: string) =>
        filterGroups['Market Filters'].allFilters.find(f => f.key === id)
          ?.key || DEFAULT_TAB,
    },
    keyDev: {
      group: 'Market Filters',
      ids: Object.values(KeyDevelopments),
      tabFn: (_: string) => KEY_DEV_TAB,
    },
  };

  for (const config of Object.values(tabRedirectMapping)) {
    const { group, ids, tabFn } = config;
    let prevFilters = prevFilterGroups?.[group].activeFilterOptions ?? [];
    let filters = filterGroups[group].activeFilterOptions;

    if (ids) {
      prevFilters = prevFilters.filter(filter => ids.includes(filter.id));
      filters = filters.filter(filter => ids.includes(filter.id));
    }

    const prevFilterMapping = prevFilters.reduce<
      Record<string, ActiveFilterOptions>
    >((mapping, prevFilter) => {
      mapping[prevFilter.id] = prevFilter;

      return mapping;
    }, {});

    const diffFilter = filters.find(filter => {
      const prevFilter = prevFilterMapping[filter.id];
      return prevFilter ? !deepEqual(filter, prevFilter) : true;
    });

    if (diffFilter) {
      return tabFn(diffFilter.id, categoryData);
    }
  }

  return categoryData
    ? categoryData.key
    : getEventsOrKeyDevsTab({ filterGroups });
};

export const getCellAlignment = (
  columnId: string,
  parentColLength: number = 0
): Property.TextAlign => {
  const isRankColumns = ['theme.rank', 'company.rank'].some(partialId =>
    columnId.includes(partialId)
  );

  if (isRankColumns) {
    return parentColLength > 1 ? 'end' : 'center';
  }

  return 'end';
};

interface Props {
  meta: ColumnMetaWithKey[];
  resetToDefault?: boolean;
  defaultColumnOrder?: ColumnMetaWithKey['field'][];
  columnOrder?: ColumnMetaWithKey['field'][];
  isCatalystInsightsEnabled?: boolean;
  isEarningsInsightsEnabled?: boolean;
}

export const getOrderedMetaFields = ({
  meta,
  resetToDefault,
  defaultColumnOrder,
  columnOrder,
  isCatalystInsightsEnabled = false,
  isEarningsInsightsEnabled = false,
}: Props) => {
  const shouldShowCatalysts =
    isCatalystInsightsEnabled || isEarningsInsightsEnabled;
  const metaFields = [
    SCREENER_ASSET_COLUMN_ID,
    ...(shouldShowCatalysts ? [SCREENER_INSIGHTS_CATALYSTS_COLUMN_ID] : []),
    SCREENER_INSIGHTS_SCENARIO_COLUMN_ID,
    ...meta.map(item => item.field),
  ];

  if (resetToDefault) {
    return defaultColumnOrder || metaFields;
  }

  if (!columnOrder) {
    return metaFields;
  }

  return metaFields.sort((a, b) => {
    const idx1 = columnOrder.indexOf(a);
    const idx2 = columnOrder.indexOf(b);

    if (idx1 === -1 || idx2 === -1) {
      return 1;
    }

    return Math.sign(idx1 - idx2);
  });
};
